/**
 * Accounts list — Roomgrid layout per the design handoff.
 *
 * Columns: row menu ("…", first) · Account name · Status (pill) ·
 * Application(s) · License · Templatify account · Provider(s) · Credits
 * (mono, from the cost-center credit balances).
 *
 * Filters apply live (contains, debounced in ListActionsBar): account name
 * inline + application / provider / cost center behind the «Filters» panel.
 * Topbar slot carries the page CTAs: Export CSV + Add account.
 */

import React from "react";
import {
  FunctionField,
  List,
  useDataProvider,
  useGetList,
  useListContext,
  useNotify,
  useRecordContext,
  useTranslate,
} from "react-admin";
import { Link } from "react-router-dom";
import {
  CustomDatagrid,
  FilterField,
  FilterAutocomplete,
  ListActionsBar,
  StatusChip,
} from "@/components/common";
import { TopbarActions } from "@/layouts";
import type { AccountListRow } from "../types/account";
import type { CreditBalanceRecord } from "@/features/credit-balance/types/creditBalance";
import { Box, Button, Chip, Typography } from "@mui/material";
import {
  Add as AddIcon,
  FileDownloadOutlined as ExportIcon,
} from "@mui/icons-material";
import { useCanManageAccounts } from "@/utils/permissions";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import {
  getAccountLookup,
  useAccountLookup,
  type LookupKey,
} from "../hooks/useAccountLookup";
import AccountRowMenu from "./AccountRowMenu";

// Wizard dropdown data, warmed up as soon as the list mounts so opening
// Add/Edit account is instant (the lookup cache is session-scoped).
const WIZARD_LOOKUPS: LookupKey[] = [
  "licenses",
  "providers",
  "costCenters",
  "notifyApps",
  "templatifyApps",
  "templatifyAccounts",
  "templatifyGroups",
];

const i18n = "resources.accounts";

const templatifyAccountOf = (record?: AccountListRow): string => {
  const acc = record?.accountResponse;
  if (!acc?.useTemplatify) return "";
  return acc.rmkTemplateAccountName || acc.rmkExistTemplateAccount?.name || "";
};

/** First 2 items as chips + a "+N" overflow chip. */
const ChipSummary: React.FC<{ names: string[] }> = ({ names }) => {
  const visible = names.filter(Boolean).slice(0, 2);
  const rest = names.length - visible.length;
  if (visible.length === 0) return <>—</>;
  return (
    <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
      {visible.map((n) => (
        <Chip key={n} size="small" label={n} />
      ))}
      {rest > 0 && <Chip size="small" label={`+${rest}`} />}
    </Box>
  );
};

const AccountNameField: React.FC = () => {
  const record = useRecordContext<AccountListRow>();
  return (
    <Typography component="span" sx={{ fontSize: "13px", fontWeight: 600 }}>
      {record?.accountResponse?.accountName ?? ""}
    </Typography>
  );
};

const AccountStatusField: React.FC = () => {
  const record = useRecordContext<AccountListRow>();
  return (
    <StatusChip
      value={record?.accountResponse?.accountStatus}
      translatePrefix={`${i18n}.status`}
    />
  );
};

const LicenseField: React.FC = () => {
  const record = useRecordContext<AccountListRow>();
  return <>{record?.licenseResponse?.name ?? "—"}</>;
};

const TemplatifyAccountField: React.FC = () => {
  const record = useRecordContext<AccountListRow>();
  const name = templatifyAccountOf(record);
  return name ? (
    <>{name}</>
  ) : (
    <Typography component="span" sx={{ fontSize: "13px", color: "text.disabled" }}>
      —
    </Typography>
  );
};

const ApplicationsField: React.FC = () => {
  const record = useRecordContext<AccountListRow>();
  return (
    <ChipSummary
      names={(record?.applicationResponse ?? []).map((a) => a.name)}
    />
  );
};

const ProvidersField: React.FC = () => {
  const record = useRecordContext<AccountListRow>();
  return (
    <ChipSummary
      names={(record?.providerResponse ?? []).map((p) => p.providerName)}
    />
  );
};

const CreditsField: React.FC<{ credits: Map<string, number> }> = ({
  credits,
}) => {
  const record = useRecordContext<AccountListRow>();
  const costCenter = record?.accountResponse?.costCenterName;
  const value = costCenter ? credits.get(costCenter) : undefined;
  return (
    <Typography
      component="span"
      className="rg-mono"
      sx={{
        fontSize: "12px",
        color: value === undefined ? "text.disabled" : "text.primary",
      }}
    >
      {value === undefined ? "—" : value.toLocaleString()}
    </Typography>
  );
};

/** CSV cell: quote when needed, keep Excel (es-ES) happy with ";". */
const csvCell = (value: string): string =>
  /[";\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const AccountsListActions: React.FC<{ credits: Map<string, number> }> = ({
  credits,
}) => {
  const translate = useTranslate();
  const canCreate = useCanManageAccounts();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { filterValues } = useListContext();
  const [exporting, setExporting] = React.useState(false);

  // Searchable filter options — applications, providers and cost centers by name.
  const { data: notifyApps } = useAccountLookup("notifyApps");
  const { data: providers } = useAccountLookup("providers");
  const { data: costCenters } = useAccountLookup("costCenters");
  const appNames = (notifyApps ?? []).map((a) => a.name).filter(Boolean);
  const providerNames = (providers ?? [])
    .map((p) => p.providerName)
    .filter(Boolean);
  const costCenterNames = (costCenters ?? []).map((c) => c.name).filter(Boolean);

  const handleExport = async (): Promise<void> => {
    if (exporting) return;
    setExporting(true);
    try {
      const { data } = await dataProvider.getList<AccountListRow>(
        "apps/accounts",
        {
          pagination: { page: 1, perPage: 10000 },
          sort: { field: "0", order: "ASC" },
          filter: filterValues ?? {},
        },
      );
      const headers = [
        translate(`${i18n}.fields.accountName`),
        translate(`${i18n}.fields.accountStatus`),
        translate(`${i18n}.fields.applications`, { _: "Application(s)" }),
        translate(`${i18n}.fields.license`),
        translate(`${i18n}.fields.templatifyAccount`, {
          _: "Templatify account",
        }),
        translate(`${i18n}.fields.providers`, { _: "Provider(s)" }),
        translate(`${i18n}.fields.credits`, { _: "Credits" }),
        translate(`${i18n}.fields.costCenter`),
      ];
      const lines = data.map((r) => {
        const acc = r.accountResponse;
        const costCenter = acc?.costCenterName ?? "";
        const balance = costCenter ? credits.get(costCenter) : undefined;
        return [
          acc?.accountName ?? "",
          acc?.accountStatus
            ? translate(`${i18n}.status.${acc.accountStatus}`, {
                _: acc.accountStatus,
              })
            : "",
          (r.applicationResponse ?? []).map((a) => a.name).join(" | "),
          r.licenseResponse?.name ?? "",
          templatifyAccountOf(r),
          (r.providerResponse ?? []).map((p) => p.providerName).join(" | "),
          balance === undefined ? "" : String(balance),
          costCenter,
        ]
          .map(csvCell)
          .join(";");
      });
      // BOM so Excel opens UTF-8 accents correctly.
      const csv =
        "﻿" + [headers.map(csvCell).join(";"), ...lines].join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `notify-accounts-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      notify(
        extractErrorMessage(error, "ra.notification.http_error", translate),
        { type: "error" },
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <TopbarActions>
        <Button
          variant="outlined"
          onClick={handleExport}
          disabled={exporting}
          startIcon={<ExportIcon sx={{ fontSize: 15 }} />}
        >
          {translate(`${i18n}.actions.exportCsv`, { _: "Export CSV" })}
        </Button>
        {canCreate && (
          <Button
            component={Link}
            to="/apps/accounts/create"
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
          >
            {translate(`${i18n}.actions.addAccount`)}
          </Button>
        )}
      </TopbarActions>
      <ListActionsBar
        title={translate(`${i18n}.name`, { smart_count: 2 })}
        filters={
          <FilterField
            source="accountName"
            label={translate(`${i18n}.filters.accountName`)}
          />
        }
        advancedFilters={
          <>
            <FilterAutocomplete
              source="applicationName"
              label={translate(`${i18n}.filters.application`)}
              options={appNames}
              sx={{ width: "100%" }}
            />
            <FilterAutocomplete
              source="providerName"
              label={translate(`${i18n}.filters.provider`)}
              options={providerNames}
              sx={{ width: "100%" }}
            />
            <FilterAutocomplete
              source="costCenterName"
              label={translate(`${i18n}.filters.costCenter`)}
              options={costCenterNames}
              sx={{ width: "100%" }}
            />
          </>
        }
      />
    </>
  );
};

export const AccountsList: React.FC = () => {
  const translate = useTranslate();

  React.useEffect(() => {
    WIZARD_LOOKUPS.forEach((key) => {
      getAccountLookup(key).catch(() => undefined);
    });
  }, []);

  // Credits live on the cost-center balances; one fetch feeds every row.
  const { data: balances } = useGetList<CreditBalanceRecord>(
    "credit-consumption/credit-balance",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "0", order: "ASC" },
    },
    // Credits are decoration here — if the balances call fails (e.g. role
    // without access) the column just shows "—" instead of toasting.
    { onError: () => undefined },
  );
  const credits = React.useMemo(() => {
    const map = new Map<string, number>();
    (balances ?? []).forEach((b) => {
      if (b.costCenterName) map.set(b.costCenterName, b.availableCredits);
    });
    return map;
  }, [balances]);

  return (
    <Box>
      <List
        actions={<AccountsListActions credits={credits} />}
        sort={{ field: "0", order: "ASC" }}
        storeKey={false}
        disableSyncWithLocation
        perPage={10}
        exporter={false}
        sx={{
          "& .RaList-main, & .RaList-content": {
            width: "100%",
            maxWidth: "100%",
          },
          "& .RaListToolbar-root, & .RaList-actions": {
            display: "flex",
            width: "100%",
            mb: 2,
            px: 0,
            py: 0,
            minHeight: "auto",
          },
          "& .RaList-actions": { flex: 1 },
        }}
      >
        <CustomDatagrid
          primarySource="accountResponse.accountName"
          rowClick={false}
          bulkActionButtons={false}
          disableMobileView
        >
          <FunctionField label="" render={() => <AccountRowMenu />} />
          <FunctionField
            source="accountResponse.accountName"
            label={translate(`${i18n}.fields.accountName`)}
            render={() => <AccountNameField />}
          />
          <FunctionField
            source="accountResponse.accountStatus"
            label={translate(`${i18n}.fields.accountStatus`)}
            render={() => <AccountStatusField />}
          />
          <FunctionField
            label={translate(`${i18n}.fields.applications`, {
              _: "Application(s)",
            })}
            render={() => <ApplicationsField />}
          />
          <FunctionField
            source="licenseResponse.name"
            label={translate(`${i18n}.fields.license`)}
            render={() => <LicenseField />}
          />
          <FunctionField
            label={translate(`${i18n}.fields.templatifyAccount`, {
              _: "Templatify account",
            })}
            render={() => <TemplatifyAccountField />}
          />
          <FunctionField
            label={translate(`${i18n}.fields.providers`, { _: "Provider(s)" })}
            render={() => <ProvidersField />}
          />
          <FunctionField
            label={translate(`${i18n}.fields.credits`, { _: "Credits" })}
            render={() => <CreditsField credits={credits} />}
          />
        </CustomDatagrid>
      </List>
    </Box>
  );
};

export default AccountsList;
